/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

// Web-only replacement for Phoenix ProductTileGroupView. Phoenix positions
// its tiles from post-mount measurements (width 0 in the server HTML, so the
// row is empty until hydration); this group lays the same tiles out with
// flexbox and CSS units, so the server HTML is already final.

import { FC } from 'react';
import { ScrollView, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import ProductTileWeb from './ProductTileWeb';
import type {
  ProductTileGroupWebViewProps,
  ProductTileWebProps,
} from './types';

export type { ProductTileGroupWebViewProps } from './types';

const SCROLL_PADDING_LEFT = 16;
const SCROLL_GAP = 8;

const scrollTileWidth = (columns: number) =>
  `calc((100vw - ${SCROLL_PADDING_LEFT + (columns - 1) * SCROLL_GAP}px) / ${columns})`;

const ProductTileGroupWebView: FC<ProductTileGroupWebViewProps> = ({
  tiles,
  layout = 'equal',
  testIDPrefix = 'productTile',
}) => {
  const renderTile = (tile: ProductTileWebProps, index: number) => (
    <ProductTileWeb
      {...tile}
      testID={tile.testID ?? `${testIDPrefix}-${index + 1}`}
    />
  );

  if (layout === 'scroll') {
    const count = tiles.length;
    const narrowColumns = count > 4 ? 4.5 : count;
    const wideColumns = Math.min(count, 7.5);
    return (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {tiles.map((tile, index) => (
          <View
            key={tile.testID ?? index}
            style={[
              styles.scrollCell(narrowColumns, wideColumns),
              index === count - 1 && styles.scrollCellLast,
            ]}
          >
            {renderTile(tile, index)}
          </View>
        ))}
      </ScrollView>
    );
  }

  return (
    <View style={layout === 'spread' ? styles.spreadRow : styles.equalRow}>
      {tiles.map((tile, index) => (
        <View
          key={tile.testID ?? index}
          style={layout === 'spread' ? styles.spreadCell : styles.equalCell}
        >
          {renderTile(tile, index)}
        </View>
      ))}
    </View>
  );
};

export default ProductTileGroupWebView;

const styles = StyleSheet.create(() => ({
  equalRow: {
    flexDirection: 'row',
  },
  equalCell: {
    flex: 1,
    alignItems: 'center',
  },
  spreadRow: {
    flexDirection: 'row',
    justifyContent: {
      xs: 'space-between',
      sm: 'center',
    },
    gap: {
      xs: 0,
      sm: SCROLL_GAP,
    },
  },
  spreadCell: {
    width: 76,
    alignItems: 'center',
  },
  scrollContent: {
    paddingLeft: SCROLL_PADDING_LEFT,
  },
  scrollCell: (narrowColumns: number, wideColumns: number) => ({
    // calc() strings pass straight through to CSS on web.
    width: {
      xs: scrollTileWidth(narrowColumns),
      sm: scrollTileWidth(wideColumns),
    } as unknown as number,
    marginRight: SCROLL_GAP,
    alignItems: 'center',
  }),
  scrollCellLast: {
    marginRight: 0,
  },
}));
